"use client";

/**
 * Drop-in for `next/navigation` (useRouter / usePathname / redirect).
 */
import { useEffect } from "react";
import {
  useNavigate,
  useRouter as useTanStackRouter,
  useRouterState,
  useParams as useTanstackParams,
} from "@tanstack/react-router";
import { parseHref } from "./href";

export function usePathname(): string {
  return useRouterState({ select: (s) => s.location.pathname });
}

export function useRouter() {
  const navigate = useNavigate();
  const router = useTanStackRouter();
  return {
    push: (href: string) => {
      const { pathname, search, hash } = parseHref(href);
      void navigate({ to: pathname as never, search: search as never, hash });
    },
    replace: (href: string) => {
      const { pathname, search, hash } = parseHref(href);
      void navigate({ to: pathname as never, search: search as never, hash, replace: true });
    },
    back: () => {
      router.history.back();
    },
    refresh: () => {
      void router.invalidate();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("mpp:refresh"));
      }
    },
    prefetch: (_href: string) => {
      /* TanStack defaultPreload=intent handles hover. Heavy Lab / Quant /
         Backtest / Pine routes are code-split via dynamic() — first visit
         loads that chunk; there is no RSC payload. */
    },
  };
}

export function useParams<T extends Record<string, string>>(): T {
  return useTanstackParams({ strict: false } as never) as T;
}

export function useSearchParams(): URLSearchParams {
  const search = useRouterState({ select: (s) => s.location.searchStr });
  return new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
}

/** Hash without a leading `#`. Empty when the URL has none. */
export function useHash(): string {
  return useRouterState({
    select: (s) => {
      const raw = String((s.location as { hash?: string }).hash ?? "");
      return raw.startsWith("#") ? raw.slice(1) : raw;
    },
  });
}

/** Render-time redirect for pages that used Next's `redirect()`. */
export function redirect(href: string): never {
  if (typeof window !== "undefined") {
    window.location.replace(href);
  }
  throw new Error(`redirect:${href}`);
}

export function Navigate({
  to,
  replace = true,
  keepSearch = false,
}: {
  to: string;
  replace?: boolean;
  keepSearch?: boolean;
}) {
  const navigate = useNavigate();
  const currentSearch = useRouterState({ select: (s) => s.location.searchStr });
  useEffect(() => {
    const parsed = parseHref(to);
    const prior = keepSearch
      ? Object.fromEntries(
          new URLSearchParams(currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch),
        )
      : {};
    void navigate({
      to: parsed.pathname as never,
      replace,
      hash: parsed.hash,
      search: { ...prior, ...parsed.search } as never,
    });
  }, [to, replace, keepSearch, currentSearch, navigate]);
  return null;
}
